import { Component, Input, Output, EventEmitter, OnChanges, OnInit, OnDestroy, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Request } from '../../services/request.service';

@Component({
  selector: 'app-request-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './request-list.component.html',
  styleUrls: ['./request-list.component.scss']
})
export class RequestListComponent implements OnChanges, OnInit, OnDestroy {
  @Input() requests: Request[] = [];
  @Input() selectedRequest: Request | null = null;
  @Input() showHistory: boolean = false;
  @Input() autoResponseThresholdMinutes: number = 3;
  @Output() selectRequest = new EventEmitter<Request>();
  @Output() refresh = new EventEmitter<void>();
  private countdownInterval?: ReturnType<typeof setInterval>;
  private nowTimestamp: number = Date.now();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.countdownInterval = setInterval(() => {
      this.nowTimestamp = Date.now();
      this.cdr.detectChanges();
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requests']) {
      console.log('RequestListComponent - Requests updated:', this.requests.length, 'requests');
      if (this.requests.length > 0) {
        console.log('First request:', this.requests[0]);
      }
    }
  }

  onSelectRequest(request: Request): void {
    this.selectRequest.emit(request);
  }

  onRefresh(): void {
    this.refresh.emit();
  }

  isSelected(request: Request): boolean {
    return this.selectedRequest?.id === request.id;
  }

  formatTime(dateString: string | undefined): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      return '-';
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      return '-';
    }
  }

  formatName(name: string): string {
    if (!name) return '';
    if (name.length <= 13) return name;

    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) return name;

    const lastName = parts.pop();
    const firstName = parts.join(' ');

    return `${firstName} ${lastName?.charAt(0)}.`;
  }

  getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      'responded': 'Yanıtlandı',
      'rejected': 'Reddedildi',
      'cancelled': 'İptal Edildi',
      'timeout': 'Zaman Aşımı',
      'timeout_out_of_hours': 'Çalışma Saati Dışı',
      'waiting': 'Bekliyor',
      // Sistem MEŞGUL durumunda otomatik yanıtlanan talepler
      'auto_responded': 'Meşgul'
    };
    return statusMap[status] || status;
  }

  shouldShowCountdown(request: Request): boolean {
    return !this.showHistory && request.status === 'waiting';
  }

  getCountdownDisplay(request: Request): string {
    const createdAtMs = new Date(request.created_at).getTime();
    if (!createdAtMs || Number.isNaN(createdAtMs)) {
      return '--:--';
    }

    const thresholdSeconds = Math.max(0, Math.floor(this.autoResponseThresholdMinutes * 60));
    const deadlineMs = createdAtMs + thresholdSeconds * 1000;
    const remainingSeconds = Math.max(0, Math.floor((deadlineMs - this.nowTimestamp) / 1000));

    return this.formatSecondsAsTimer(remainingSeconds);
  }

  private formatSecondsAsTimer(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}

